/**
 * src/data/seoLocations.js
 *
 * Programmatic SEO data: 20 US cities + 10 Canadian cities.
 * Each entry powers a /locations/:slug page rendered by LocationPage.jsx.
 *
 * Image strategy:
 *   - Hero images use Unsplash (free, commercially licensed)
 *   - Each image is a curated photo ID known to be stable
 *   - URL: https://images.unsplash.com/photo-<id>?auto=format&fit=crop&w=1600&q=80
 *
 * Each city has:
 *   slug         kebab-case URL slug
 *   city         display name
 *   region       state/province
 *   country      "USA" or "Canada"
 *   hero         Unsplash skyline photo URL
 *   hook         one-line local angle (used in hero subhead)
 *   localContext 2–3 sentence "what's true about doing business in this city"
 *   caseStudy    fake but realistic case study tailored to the city
 *                  { title, industry, problem, fix, result }
 */

const u = (id) =>
  `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=1600&q=80`;

// Generic high-quality skylines / urban shots — verified-working Unsplash IDs.
// We cycle a small confirmed set; the SEO pages now also feature a 6-product
// carousel as the main visual focus, so the hero photo is supporting context
// rather than the centrepiece.
const SKYLINE_A = u("1485871981521-5b1fd3805eee"); // NYC orange skyline
const SKYLINE_B = u("1518391846015-55a9cc003b25"); // city dusk
const SKYLINE_C = u("1444723121867-7a241cacace9"); // city aerial
const SKYLINE_D = u("1480714378408-67cf0d13bc1b"); // NYC daytime
const SKYLINE_E = u("1449034446853-66c86144b0ad"); // city lights
const SKYLINE_F = u("1496442226666-8d4d0e62e6e9"); // downtown buildings

export const SEO_LOCATIONS = [
  // ─── United States ──────────────────────────────────────────────────────
  {
    slug: "new-york",
    city: "New York",
    region: "New York",
    country: "USA",
    hero: SKYLINE_D,
    hook: "Where attention is the most expensive resource on earth.",
    localContext:
      "New York founders compete in the densest brand market in North America. Every category has incumbents with eight-figure ad budgets. The win condition is not louder; it is sharper — a brand voice that survives a Times Square attention diet and a funnel that respects how fast a New Yorker bounces.",
    caseStudy: {
      title: "SoHo skincare brand cut acquisition cost 38% in 60 days",
      industry: "DTC beauty",
      problem:
        "Spending $94 to acquire a $62 first-purchase customer. Beautiful Shopify site, no diagnostic clarity on which step in the funnel was actually leaking.",
      fix: "Marketing Audit identified two leaks — a 41% drop-off on shipping reveal and a vague subscription offer. Re-sequenced the PDP, added a transparent shipping module, rewrote the subscription value prop.",
      result: "CAC down to $58 within 60 days. Subscription conversion up 2.3x. Strategy Call → 90-day implementation.",
    },
  },
  {
    slug: "los-angeles",
    city: "Los Angeles",
    region: "California",
    country: "USA",
    hero: SKYLINE_F,
    hook: "Where the brand is half the product.",
    localContext:
      "LA founders sell vibe as much as utility. A site that looks generic gets dismissed before the offer is even read. We've worked with creator brands, beauty labels, and consultancies who needed strategy that respected the aesthetic stakes — and a funnel that converted the audience the brand was already attracting.",
    caseStudy: {
      title: "Beverly Hills consultancy 3x'd qualified inbound in one quarter",
      industry: "Premium B2B consulting",
      problem:
        "Inbound was strong on volume, terrible on quality — 90% of discovery calls were sub-$50k accounts when the offer targeted $250k engagements.",
      fix: "Brand Transformation Workshop reframed positioning, voice, and the lead-magnet stack. New ICP-specific landing page and a price-anchored audit offer screened out the wrong leads upfront.",
      result: "Sub-$50k inquiries dropped to 12%. Pipeline value 3.1x in 90 days.",
    },
  },
  {
    slug: "chicago",
    city: "Chicago",
    region: "Illinois",
    country: "USA",
    hero: SKYLINE_A,
    hook: "Where the Midwest meets serious B2B revenue.",
    localContext:
      "Chicago founders run capital-efficient businesses with real margins and real customers. The market rewards substance over flash. Our work here tends to be 90-day implementations and Concierge Funnels — buyers who know what they need and value execution over branding theater.",
    caseStudy: {
      title: "Loop-based SaaS doubled trial-to-paid in 90 days",
      industry: "Vertical SaaS",
      problem:
        "Strong demo conversion but a 4.1% trial-to-paid rate. Onboarding emails read like internal docs and the upgrade flow buried the value prop.",
      fix: "90-Day Revenue Regeneration: rebuilt the onboarding email sequence, redesigned the upgrade page with a clear before/after delta, installed analytics that surfaced the drop-off step.",
      result: "Trial-to-paid up to 8.7%. MRR added in the last 30 days exceeded the entire prior quarter.",
    },
  },
  {
    slug: "houston",
    city: "Houston",
    region: "Texas",
    country: "USA",
    hero: SKYLINE_C,
    hook: "Where energy money meets emerging tech.",
    localContext:
      "Houston is a relationship economy on a global market. Energy, healthcare, and emerging tech live side by side, and most decisions still travel through introductions. Our engagements here typically pair brand strategy with conversion infrastructure — credibility that survives a referral and a funnel that captures it.",
    caseStudy: {
      title: "Energy services firm turned referrals into a measurable channel",
      industry: "Industrial services",
      problem:
        "Pipeline was 90% referrals with no system to track, nurture, or scale them. New introductions evaporated within a week.",
      fix: "Concierge Funnels + Strategy Call: built a 'referred lead' landing page, automated nurture sequence, and CRM tagging that surfaced re-engaged opportunities.",
      result: "Referral close rate up 41%. Three new accounts attributed directly to the new flow in Q1.",
    },
  },
  {
    slug: "phoenix",
    city: "Phoenix",
    region: "Arizona",
    country: "USA",
    hero: SKYLINE_B,
    hook: "Where transplants outnumber locals and brand-from-scratch is the norm.",
    localContext:
      "Phoenix has been one of the fastest-growing metros in the US for a decade. Most founders here are building for a transient audience and don't have the luxury of word-of-mouth alone. Our work here leans heavily on local SEO, paid acquisition, and brand systems that scale across a sprawling metro.",
    caseStudy: {
      title: "Scottsdale med-spa added six-figure recurring revenue in one quarter",
      industry: "Medical aesthetics",
      problem:
        "Heavy paid spend with shallow ROI — patients booked one treatment then disappeared. No subscription or membership story.",
      fix: "Brand Transformation Workshop rebuilt the offer ladder around a membership tier. New pricing page, new email sequence, new in-cabin upsell script.",
      result: "Membership revenue jumped from $0 to $43k/mo in 90 days. Patient retention up 2.8x.",
    },
  },
  {
    slug: "philadelphia",
    city: "Philadelphia",
    region: "Pennsylvania",
    country: "USA",
    hero: SKYLINE_E,
    hook: "Where heritage brands compete with insurgent challengers.",
    localContext:
      "Philadelphia is a city of long-standing businesses with deep customer relationships. The challenge: many of them have websites that haven't been updated since 2014. Our work here is often a 90-day modernization that preserves the brand equity while unlocking a younger demographic.",
    caseStudy: {
      title: "Center City law firm modernized intake without alienating long-term clients",
      industry: "Legal services",
      problem:
        "98% of new client intake happened by phone. Younger prospects ghosting at the call-to-book step.",
      fix: "Hybrid intake: kept phone primary but added a self-serve intake form with a calendar booking flow. Strategy Call to sequence the launch.",
      result: "Self-serve bookings reached 40% of intake within 6 months. Long-term clients unaffected.",
    },
  },
  {
    slug: "san-antonio",
    city: "San Antonio",
    region: "Texas",
    country: "USA",
    hero: SKYLINE_F,
    hook: "Where bilingual buyers move different ad dollars.",
    localContext:
      "San Antonio's market reality includes a significant bilingual customer base. Brands that lazily translate English copy underperform brands that build the messaging from the audience inward. Our work here usually involves dual-track campaigns and culturally specific funnel architecture.",
    caseStudy: {
      title: "Local home services brand grew bilingual leads 4x",
      industry: "Home services",
      problem:
        "English landing page converting 6%, Spanish landing page (a literal translation) converting 1.2%. Spend on Spanish ads being wasted.",
      fix: "Rewrote the Spanish funnel from audience-first — different headlines, different proof points, different review screenshots.",
      result: "Spanish landing page hit 5.8% conversion. Cost per lead halved.",
    },
  },
  {
    slug: "san-diego",
    city: "San Diego",
    region: "California",
    country: "USA",
    hero: SKYLINE_C,
    hook: "Where lifestyle brands hide real DTC machines.",
    localContext:
      "San Diego founders sell lifestyle without sounding like they're trying. The brands that win here look casual and operate ruthlessly — strong unit economics behind a beach-easy aesthetic. Our work is usually about tightening the back-end while keeping the front-end on-brand.",
    caseStudy: {
      title: "North County apparel brand cut returns 22%",
      industry: "DTC apparel",
      problem:
        "37% return rate on online apparel orders — sizing confusion. Profit margin disappearing into reverse logistics.",
      fix: "Marketing Audit identified PDP sizing copy as the leak. Built an interactive sizing tool and added 'true-to-size' video reviews from real customers.",
      result: "Return rate dropped to 28.7% in two months. Net margin recovered.",
    },
  },
  {
    slug: "dallas",
    city: "Dallas",
    region: "Texas",
    country: "USA",
    hero: SKYLINE_A,
    hook: "Where enterprise-grade ambition meets agency fatigue.",
    localContext:
      "Dallas is one of the most agency-saturated metros in the US — most founders here have been burned twice before they call us. We tend to win the business by being the third option: no theatrical pitch deck, just a Marketing Audit that proves we understand the bottleneck before we propose anything.",
    caseStudy: {
      title: "Uptown B2B SaaS replaced three agencies with one Bow Tie engagement",
      industry: "B2B SaaS",
      problem:
        "Paying $34k/mo across three agencies (paid, content, design) — none of them talking to each other. Pipeline flat.",
      fix: "90-Day Revenue Regeneration consolidated all three workstreams under one ICP, one funnel, one measurement stack.",
      result: "Cost reduced 41%. Pipeline value up 2.4x. Renewed for a second 90-day.",
    },
  },
  {
    slug: "austin",
    city: "Austin",
    region: "Texas",
    country: "USA",
    hero: SKYLINE_B,
    hook: "Where every founder has a personal brand and a thesis.",
    localContext:
      "Austin runs on personal brand. A first-time founder's LinkedIn matters more than the company logo. Our engagements here often involve Brand Transformation Workshops that separate the founder narrative from the company narrative — so the business can scale without the founder being its only growth lever.",
    caseStudy: {
      title: "East Austin solo founder built a 4-person team without losing the founder edge",
      industry: "Boutique agency",
      problem:
        "Personal brand drove all inbound but team hires couldn't reproduce it. Founder bottlenecked every deal.",
      fix: "Workshop output: separated 'founder POV' from 'company method.' New service pages led with method; sales team trained to sell without name-dropping the founder.",
      result: "Founder time in sales calls dropped 70%. Team-led deals closed at 84% of founder rate.",
    },
  },
  {
    slug: "jacksonville",
    city: "Jacksonville",
    region: "Florida",
    country: "USA",
    hero: SKYLINE_E,
    hook: "Where local pride pays off when the brand earns it.",
    localContext:
      "Jacksonville's market rewards businesses that show up consistently for the local community. Brands that act like out-of-state operators get treated like outsiders. Our work here is often about codifying the local voice into the brand system — so the marketing doesn't accidentally come off as generic.",
    caseStudy: {
      title: "Riverside boutique fitness studio became the local default",
      industry: "Boutique fitness",
      problem:
        "Three studios within two miles competing on price. Generic brand voice, generic Instagram, no positioning.",
      fix: "Workshop reframed the studio as 'the one for working parents.' Voice, schedule, on-site experience all rebuilt around that ICP.",
      result: "Membership grew 2.6x in 6 months. Two competitor studios closed.",
    },
  },
  {
    slug: "san-francisco",
    city: "San Francisco",
    region: "California",
    country: "USA",
    hero: SKYLINE_F,
    hook: "Where the bar for design is pre-set at 'beautiful by default.'",
    localContext:
      "SF founders are spoiled by world-class design as table stakes. The opportunity is not to be the prettiest — it's to be the clearest. Our work here often pairs the Brand Transformation Workshop with the Complete Brand Identity System: positioning depth plus polished execution.",
    caseStudy: {
      title: "SOMA fintech rebuilt its homepage messaging in one workshop",
      industry: "Fintech",
      problem:
        "Beautiful site, gorgeous design system — sub-1% homepage-to-signup. Buyers couldn't tell what made them different from three other YC-backed competitors.",
      fix: "Brand Transformation Workshop forced the team to make positioning trade-offs they'd been avoiding. New homepage shipped in 2 weeks.",
      result: "Signup conversion 4.1x. Sales cycle shortened by 11 days on average.",
    },
  },
  {
    slug: "seattle",
    city: "Seattle",
    region: "Washington",
    country: "USA",
    hero: SKYLINE_C,
    hook: "Where engineering culture writes copy like a spec.",
    localContext:
      "Seattle's tech founders tend to over-explain and under-sell. Pages read like product documentation. Our work here is often about translating engineering-grade thinking into customer-grade language — without dumbing down the precision.",
    caseStudy: {
      title: "Bellevue dev tool brand finally explained itself in one sentence",
      industry: "Developer tools",
      problem:
        "Homepage hero was a 3-paragraph technical explanation. Even the founders couldn't agree on the one-sentence pitch.",
      fix: "Strategy Call forced a 90-minute positioning exercise; resulting headline shipped same week.",
      result: "Inbound demo requests up 2.1x. Sales team adopted the new one-liner verbatim.",
    },
  },
  {
    slug: "denver",
    city: "Denver",
    region: "Colorado",
    country: "USA",
    hero: SKYLINE_D,
    hook: "Where outdoor lifestyle brands and serious B2B coexist.",
    localContext:
      "Denver's brand-and-business duality is real — half our clients here are recreation/lifestyle, half are serious B2B. The common thread: founders who hike on the weekend and run lean operations on weekdays. They want partners who match the cadence: no fluff, fast iteration, measurable wins.",
    caseStudy: {
      title: "RiNo wellness studio went from $0 to $1.2M ARR in 18 months",
      industry: "Wellness retail",
      problem:
        "Pre-revenue brand with strong concept and zero infrastructure. Founder doing everything.",
      fix: "Complete Brand Identity System + Concierge Funnels: built the brand stack and the conversion architecture in parallel.",
      result: "Soft-launched at $14k MRR. 18 months later: $1.2M ARR.",
    },
  },
  {
    slug: "boston",
    city: "Boston",
    region: "Massachusetts",
    country: "USA",
    hero: SKYLINE_A,
    hook: "Where institutions buy from operators who can explain themselves.",
    localContext:
      "Boston buyers — universities, hospitals, biotech — buy from sellers who can document their methodology. The opening pitch isn't a video; it's a one-pager that can survive a procurement review. Our work here often involves Marketing Audits and structured sales-enablement assets.",
    caseStudy: {
      title: "Cambridge B2B consulting won a $400k engagement with one new pager",
      industry: "Healthcare consulting",
      problem:
        "Losing to bigger firms on RFPs because the proposal didn't read 'institutional' enough. Brand felt like a one-person shop.",
      fix: "Brand Transformation Workshop output included an RFP-ready methodology document. Brand voice tightened to read peer-to-peer with institutional buyers.",
      result: "Won a $400k engagement within the next two RFPs.",
    },
  },
  {
    slug: "miami",
    city: "Miami",
    region: "Florida",
    country: "USA",
    hero: SKYLINE_F,
    hook: "Where money moves fast and brands have one quarter to prove themselves.",
    localContext:
      "Miami is the fastest-cycling market in the US — fashion, fintech, crypto, real estate all compress to 90-day buying cycles. Founders here can't afford 6-month brand projects. Our work fits: 3-day workshops, 90-day implementations, real outcomes within the cycle.",
    caseStudy: {
      title: "Brickell real estate brokerage flipped pipeline in 60 days",
      industry: "Real estate",
      problem:
        "Hot inventory, weak inbound. Generic 'luxury living' messaging in a market saturated with 'luxury living' messaging.",
      fix: "Workshop reframed brand around 'investment outcomes' instead of 'lifestyle marketing.' New campaign in 21 days.",
      result: "Buyer-side leads up 73% in 60 days. Two over-asking offers in 30.",
    },
  },
  {
    slug: "atlanta",
    city: "Atlanta",
    region: "Georgia",
    country: "USA",
    hero: SKYLINE_B,
    hook: "Where Black founders, fintech, and media converge.",
    localContext:
      "Atlanta is one of the most dynamic founder cities in the US — Black-owned businesses, fintech, media production, and logistics all running in parallel. The shared trait: founders who want strategy partners who actually understand the cultural undertow of the brand they're building.",
    caseStudy: {
      title: "Midtown media brand 5x'd sponsorship revenue in 9 months",
      industry: "Independent media",
      problem:
        "Strong audience, weak monetization. Sponsorship deck looked generic; sponsors couldn't tell what made the audience valuable.",
      fix: "Brand Transformation Workshop rebuilt audience narrative around 3 buyer-specific value props. New deck, new outreach sequence.",
      result: "Sponsorship revenue 5.2x in 9 months. First 6-figure deal closed in month 4.",
    },
  },
  {
    slug: "minneapolis",
    city: "Minneapolis",
    region: "Minnesota",
    country: "USA",
    hero: SKYLINE_E,
    hook: "Where Fortune 500 buyers live next door to indie founders.",
    localContext:
      "Minneapolis has more Fortune 500 HQs per capita than almost any US city — and a quietly thriving indie founder scene. Our engagements here often involve helping smaller brands sell upstream into enterprise without losing the founder character that got them noticed.",
    caseStudy: {
      title: "North Loop B2B agency landed two Fortune 500 retainers",
      industry: "B2B services",
      problem:
        "Sole proprietor with strong portfolio, no positioning to sell upstream. Enterprise procurement kept stalling.",
      fix: "Workshop output: methodology doc + ICP-specific case study set + tiered pricing page that signaled enterprise-readiness.",
      result: "Two Fortune 500 retainers signed within 12 months. Revenue 4.1x.",
    },
  },
  {
    slug: "portland",
    city: "Portland",
    region: "Oregon",
    country: "USA",
    hero: SKYLINE_C,
    hook: "Where craft brands compete with values, not volume.",
    localContext:
      "Portland buyers buy brands that match their values. The opportunity is genuine alignment, not performative messaging. Our work here is often about codifying the brand's actual values into voice and operations — so it survives scaling without becoming generic.",
    caseStudy: {
      title: "Pearl District craft food brand expanded to 14 retail SKUs",
      industry: "CPG food",
      problem:
        "Strong direct-to-consumer brand, no retail strategy. Buyers couldn't picture the brand on a shelf.",
      fix: "Complete Brand Identity System: shelf-ready packaging system, brand book, sell-sheets, retail playbook.",
      result: "Picked up by 4 regional retailers in 6 months. 14 SKUs by year end.",
    },
  },
  {
    slug: "las-vegas",
    city: "Las Vegas",
    region: "Nevada",
    country: "USA",
    hero: SKYLINE_A,
    hook: "Where the experience IS the marketing.",
    localContext:
      "Vegas operators understand spectacle, but most underestimate the off-strip economy of repeat locals. Our work here is often about building loyalty systems for businesses that have been over-relying on tourist traffic.",
    caseStudy: {
      title: "Off-strip restaurant tripled locals revenue with a membership program",
      industry: "Restaurants",
      problem:
        "Heavy tourist traffic, no local repeat business. Slow Tuesdays, packed Saturdays — uneven cash flow.",
      fix: "Concierge Funnels designed a locals-only membership with tiered perks. Built the landing page, automation, and in-house enrollment script.",
      result: "Membership hit 480 members in 5 months. Tuesday covers up 2.4x.",
    },
  },

  // ─── Canada ────────────────────────────────────────────────────────────
  {
    slug: "toronto",
    city: "Toronto",
    region: "Ontario",
    country: "Canada",
    hero: SKYLINE_D,
    hook: "Canada's most competitive consumer market.",
    localContext:
      "Toronto founders compete in the densest brand market in Canada. Real estate, fintech, fashion, healthtech — every category has well-funded incumbents. The win condition is sequencing: which lever moves revenue this quarter, which lever moves next quarter.",
    caseStudy: {
      title: "Yonge Street fintech raised conversion 3x without changing the design",
      industry: "Consumer fintech",
      problem:
        "Beautiful product, 1.8% homepage conversion. Investors pushing for paid acquisition before fixing the leak.",
      fix: "Marketing Audit identified the value-prop ambiguity. Strategy Call sequenced the copy fix first, paid second.",
      result: "Conversion to 5.9% in 6 weeks. Saved the paid budget.",
    },
  },
  {
    slug: "vancouver",
    city: "Vancouver",
    region: "British Columbia",
    country: "Canada",
    hero: SKYLINE_F,
    hook: "Where wellness, real estate, and tech share the same buyer.",
    localContext:
      "Vancouver's economy is unusually integrated — the same buyer might own real estate, invest in a wellness brand, and run a SaaS side-business. Our work here often involves brands that need to speak to a cross-vertical audience without sounding diffuse.",
    caseStudy: {
      title: "Kitsilano wellness brand consolidated three audiences into one positioning",
      industry: "Wellness DTC",
      problem:
        "Trying to talk to wellness consumers, retail investors, and B2B wholesale buyers from the same homepage. Converting none of them well.",
      fix: "Workshop output: separate funnel doorways for each audience, shared brand voice, shared visual language.",
      result: "Wholesale inquiries up 4x. DTC conversion stable. Investor outreach formalized.",
    },
  },
  {
    slug: "montreal",
    city: "Montreal",
    region: "Quebec",
    country: "Canada",
    hero: SKYLINE_E,
    hook: "Where bilingual brand systems are non-negotiable.",
    localContext:
      "Montreal brands ship in two languages — and a translated site rarely converts as well as a properly localized one. Our work in Montreal usually pairs the Brand Transformation Workshop with a deliberate French-first messaging build.",
    caseStudy: {
      title: "Old Montreal SaaS shipped a fully bilingual experience without halving its team",
      industry: "B2B SaaS",
      problem:
        "English-only product with French marketing pages translated by agency. French conversion was 30% of English.",
      fix: "Concierge Funnels rebuilt the French side from scratch with French-native copy, French case studies, and a French-speaking sales call routing.",
      result: "French-side conversion reached parity in 90 days. Total Quebec MRR up 2.6x.",
    },
  },
  {
    slug: "calgary",
    city: "Calgary",
    region: "Alberta",
    country: "Canada",
    hero: SKYLINE_A,
    hook: "Where energy, tech, and entrepreneurship share one downtown.",
    localContext:
      "Calgary is Bow Tie Kreative's home turf. Energy money has shifted toward diversified investing; tech is finally rising; and small business is the most pragmatic in the country. Our local clients value plain talk and measurable outcomes more than anywhere else we work.",
    caseStudy: {
      title: "Downtown Calgary SaaS spun out from oil & gas consulting into $2M ARR",
      industry: "Vertical SaaS",
      problem:
        "Spinout from an oil & gas consultancy with strong methodology but no consumer-facing brand. Founder couldn't tell the SaaS story without describing the consulting it came from.",
      fix: "Brand Transformation Workshop separated the SaaS narrative from the consulting roots. 90-Day Revenue Regeneration built the actual funnel.",
      result: "Closed first 12 SaaS customers in 90 days. Hit $2M ARR in year 2.",
    },
  },
  {
    slug: "edmonton",
    city: "Edmonton",
    region: "Alberta",
    country: "Canada",
    hero: SKYLINE_B,
    hook: "Where service businesses still dominate the local economy.",
    localContext:
      "Edmonton's economy leans heavily on local service businesses — home services, healthcare, professional services. The brand bar in this market is low, so the founders who invest in proper positioning leap ahead of competitors who haven't updated since 2017.",
    caseStudy: {
      title: "Old Strathcona home services brand became the obvious choice in two seasons",
      industry: "Home services",
      problem:
        "Strong reviews, weak positioning. Competing on price against operators with worse work and lower margins.",
      fix: "Workshop reframed positioning around 'work that survives an Alberta winter.' New visual identity and a guarantee-led offer.",
      result: "Average ticket up 38%. Lead-to-booking rate doubled.",
    },
  },
  {
    slug: "ottawa",
    city: "Ottawa",
    region: "Ontario",
    country: "Canada",
    hero: SKYLINE_C,
    hook: "Where government contracts and indie founders intersect.",
    localContext:
      "Ottawa's economy is shaped by federal procurement, but the most exciting work happens with founders who refuse to be procurement-dependent. Our engagements here often help these founders build commercial brand presence that opens private-sector accounts.",
    caseStudy: {
      title: "Centretown agency landed three private-sector accounts without losing federal work",
      industry: "Professional services",
      problem:
        "Brand read 100% government. Private-sector prospects bounced because the messaging didn't speak to them.",
      fix: "Brand Transformation Workshop output included a parallel commercial-facing site with a different voice and ICP.",
      result: "Three private-sector accounts won in 6 months. Federal pipeline untouched.",
    },
  },
  {
    slug: "winnipeg",
    city: "Winnipeg",
    region: "Manitoba",
    country: "Canada",
    hero: SKYLINE_D,
    hook: "Where pragmatic founders build resilient revenue.",
    localContext:
      "Winnipeg founders run capital-disciplined businesses with long-term customer relationships. The market doesn't reward hype. Our work here tends to be tactical: Marketing Audits, Concierge Funnels, and 90-day implementations that compound revenue without inflating the brand budget.",
    caseStudy: {
      title: "Exchange District manufacturer added a $250k product line through ecommerce",
      industry: "B2B manufacturing",
      problem:
        "$5M B2B revenue with a $0 ecommerce play despite a clear consumer adjacent product.",
      fix: "Strategy Call sequenced a phased ecommerce build. Concierge Funnels delivered the actual implementation.",
      result: "$250k consumer revenue in year one without disrupting the B2B core.",
    },
  },
  {
    slug: "halifax",
    city: "Halifax",
    region: "Nova Scotia",
    country: "Canada",
    hero: SKYLINE_E,
    hook: "Where Atlantic Canadian founders build for national markets.",
    localContext:
      "Halifax founders often build national brands from a regional base. Our work here is about packaging the brand so it sells anywhere in Canada — without losing the genuine East-Coast character that gave the business its first 100 customers.",
    caseStudy: {
      title: "Halifax DTC seafood brand expanded to all 10 provinces in 14 months",
      industry: "Specialty food DTC",
      problem:
        "Strong Maritime brand, weak national appeal. Shipping math wasn't packaged correctly for distant customers.",
      fix: "Complete Brand Identity System reworked the national-facing pages and shipping value prop.",
      result: "Online revenue 3.4x in 14 months. Now shipping to every Canadian province.",
    },
  },
  {
    slug: "quebec-city",
    city: "Quebec City",
    region: "Quebec",
    country: "Canada",
    hero: SKYLINE_F,
    hook: "Where French-first brands outsell bilingual brands.",
    localContext:
      "In Quebec City, French isn't a translation — it's the brand. Brands built bilingual-from-start consistently outperform brands that retrofit French later. Our work here is most often a Brand Transformation Workshop that re-foundations the entire brand around the French-speaking customer.",
    caseStudy: {
      title: "Old Quebec retailer 2.5x'd online conversion with a French-native rebuild",
      industry: "Retail / DTC",
      problem:
        "Site was English-built, French-translated. French shoppers — the actual majority — converted at 30% of the English rate.",
      fix: "Workshop output: French-native homepage, French-native PDP voice, French-native checkout copy.",
      result: "French conversion 2.5x. Total revenue up 41%.",
    },
  },
  {
    slug: "hamilton",
    city: "Hamilton",
    region: "Ontario",
    country: "Canada",
    hero: SKYLINE_A,
    hook: "Where industrial heritage meets the modern creator economy.",
    localContext:
      "Hamilton is a city in active reinvention — manufacturers next to creative founders, both serving Toronto-area customers at better margins. Our work here often involves helping founders position around the 'Hamilton edge' instead of trying to look like a downtown Toronto brand.",
  },

  // ─── Wave 2 — additional US metros ──────────────────────────────────────
  {
    slug: "nashville",
    city: "Nashville",
    region: "Tennessee",
    country: "USA",
    hero: SKYLINE_B,
    hook: "Where every brand has to sing for its supper.",
    localContext:
      "Nashville is one of the fastest-growing creator-economy metros in the US — music, healthcare, and tech all built on a personality-first brand culture. The brands that win here don't just sound professional; they sound like someone. Our work tightens the founder voice into a system the rest of the team can carry.",
  },
  {
    slug: "charlotte",
    city: "Charlotte",
    region: "North Carolina",
    country: "USA",
    hero: SKYLINE_C,
    hook: "Where Southeast banking money meets startup ambition.",
    localContext:
      "Charlotte's combination of finance, fintech, and emerging consumer brands creates buyers with high standards and short attention spans. Our work here tends to lean into clean credibility architecture — a brand that signals 'serious' before it signals 'creative.'",
  },
  {
    slug: "detroit",
    city: "Detroit",
    region: "Michigan",
    country: "USA",
    hero: SKYLINE_D,
    hook: "Where comeback brands set the tempo.",
    localContext:
      "Detroit founders build with grit and don't tolerate fluff. The market rewards substance, story, and operational discipline. Our engagements here often involve modernizing legacy operations without losing the toughness that built the customer base.",
  },
  {
    slug: "tampa",
    city: "Tampa",
    region: "Florida",
    country: "USA",
    hero: SKYLINE_E,
    hook: "Where the new-money in-migration is rewriting the buyer base.",
    localContext:
      "Tampa has absorbed more high-net-worth transplants in the last five years than almost any US metro. The buyer base has shifted faster than most brands' messaging — and the ones that update first capture the upgrade dollars. Our work here is often a 90-day repositioning to match the new audience.",
  },
  {
    slug: "indianapolis",
    city: "Indianapolis",
    region: "Indiana",
    country: "USA",
    hero: SKYLINE_F,
    hook: "Where midwestern reliability scales into the seven figures.",
    localContext:
      "Indianapolis runs on operationally disciplined businesses — manufacturing, logistics, B2B services. The brands that scale fastest here pair sharp positioning with relentless execution. Our work tends to be Marketing Audits and 90-day implementations that compound.",
  },
  {
    slug: "columbus",
    city: "Columbus",
    region: "Ohio",
    country: "USA",
    hero: SKYLINE_A,
    hook: "Where insurance, retail, and edtech HQs share one downtown.",
    localContext:
      "Columbus is one of the most under-rated B2B metros in the US — Fortune 500 headquarters next to a thriving founder community. We help smaller brands sell upstream into the enterprise neighbors without losing the founder character that started the business.",
  },
  {
    slug: "kansas-city",
    city: "Kansas City",
    region: "Missouri",
    country: "USA",
    hero: SKYLINE_B,
    hook: "Where logistics money meets BBQ-grade brand loyalty.",
    localContext:
      "Kansas City's economy is built on logistics, agriculture-tech, and a thick layer of family-owned brands. Loyalty runs deep but doesn't extend to outsiders who don't earn it. Our work helps brands signal 'local, but built to scale' without sounding parochial.",
  },
  {
    slug: "sacramento",
    city: "Sacramento",
    region: "California",
    country: "USA",
    hero: SKYLINE_C,
    hook: "Where capital-city stability funds quietly ambitious founders.",
    localContext:
      "Sacramento is California without the SF/LA pressure — founders who move fast but capitalize conservatively. Our work here often involves polishing pre-revenue brands into investor-ready packages and locking in conversion before the seed round.",
  },
  {
    slug: "salt-lake-city",
    city: "Salt Lake City",
    region: "Utah",
    country: "USA",
    hero: SKYLINE_D,
    hook: "Where SaaS, outdoor, and direct-sales empires share one valley.",
    localContext:
      "Salt Lake City's tech corridor builds operationally sophisticated companies on the back of strong unit economics. Brands here tend to look more polished than they sound — we tighten the voice so it can survive an enterprise sales cycle.",
  },
  {
    slug: "raleigh",
    city: "Raleigh",
    region: "North Carolina",
    country: "USA",
    hero: SKYLINE_E,
    hook: "Where the Research Triangle's PhDs become founders.",
    localContext:
      "Raleigh-Durham's startup density is in the top 10 nationally and the buyers are unusually technical. The trap most founders fall into: writing for peers instead of customers. Our work here translates research-grade thinking into market-grade language.",
  },

  // ─── Wave 2 — additional Canadian metros ───────────────────────────────
  {
    slug: "mississauga",
    city: "Mississauga",
    region: "Ontario",
    country: "Canada",
    hero: SKYLINE_F,
    hook: "Where head-office ambition lives next door to entrepreneurial freedom.",
    localContext:
      "Mississauga's economy mixes Fortune 500 Canadian HQs with a deep bench of independent founders. The buyers who matter most can spot a polished agency from a kilometre away. Our work here lifts brand discipline to head-office standard while keeping founder-led costs in check.",
  },
  {
    slug: "brampton",
    city: "Brampton",
    region: "Ontario",
    country: "Canada",
    hero: SKYLINE_A,
    hook: "Where multi-cultural buyer bases reward brands that speak their language.",
    localContext:
      "Brampton is one of Canada's most diverse cities by foreign-language usage. Brands that culturally segment their funnels here outperform brands that translate them. Our work often involves audience-specific funnel architecture rather than one-size-fits-all messaging.",
  },
  {
    slug: "surrey",
    city: "Surrey",
    region: "British Columbia",
    country: "Canada",
    hero: SKYLINE_B,
    hook: "Where the fastest-growing BC city is rebuilding its brand in real time.",
    localContext:
      "Surrey is one of the fastest-growing cities in Canada and its commercial base is shifting from suburb-of-Vancouver into a destination economy. Brands here are competing for a new identity. Our work helps founders position around the upgrade — not the legacy.",
  },
  {
    slug: "burnaby",
    city: "Burnaby",
    region: "British Columbia",
    country: "Canada",
    hero: SKYLINE_C,
    hook: "Where gaming, animation, and tech HQs cluster.",
    localContext:
      "Burnaby's economy is unusually concentrated in creative-tech: gaming studios, animation, and SaaS. Buyers here are visually literate and impatient with generic design. Our work tends to lean into the Complete Brand Identity System for clients who need the visual bar raised first.",
  },
  {
    slug: "london-on",
    city: "London",
    region: "Ontario",
    country: "Canada",
    hero: SKYLINE_D,
    hook: "Where a university town graduates into Canada's mid-market hub.",
    localContext:
      "London Ontario's mix of healthcare, insurance, and emerging tech creates a stable but ambitious market. Founders here value pragmatic over flashy. Our engagements lean toward 90-day implementations that produce measurable inbound by the end of the quarter.",
  },
  {
    slug: "kitchener",
    city: "Kitchener",
    region: "Ontario",
    country: "Canada",
    hero: SKYLINE_E,
    hook: "Where Waterloo's tech founders quietly out-build the rest of the country.",
    localContext:
      "The Kitchener-Waterloo corridor produces some of Canada's most operationally sound startups. The challenge: the brand often lags the engineering. Our work here translates technical excellence into a customer-facing story that can carry a sales cycle.",
  },
  {
    slug: "saskatoon",
    city: "Saskatoon",
    region: "Saskatchewan",
    country: "Canada",
    hero: SKYLINE_F,
    hook: "Where agri-tech, mining, and small-town brand loyalty intersect.",
    localContext:
      "Saskatoon's commercial base is unusually loyal — but won't excuse a website that looks like 2014. Our work modernizes brand surfaces without disrupting the long-running relationships founders depend on.",
  },
  {
    slug: "regina",
    city: "Regina",
    region: "Saskatchewan",
    country: "Canada",
    hero: SKYLINE_A,
    hook: "Where prairie pragmatism funds quietly profitable businesses.",
    localContext:
      "Regina founders run lean, well-margined companies and don't waste money. Our engagements here open with a Marketing Audit so the spend can be sequenced — never a redesign for redesign's sake.",
  },
  {
    slug: "windsor",
    city: "Windsor",
    region: "Ontario",
    country: "Canada",
    hero: SKYLINE_B,
    hook: "Where cross-border B2B opens an entire second market.",
    localContext:
      "Windsor's adjacency to Detroit makes it a binational economy by default. Brands that build cross-border ready collateral — pricing, compliance, brand voice — outperform brands that pretend the border isn't there. Our work often involves dual-market positioning.",
  },
  {
    slug: "victoria",
    city: "Victoria",
    region: "British Columbia",
    country: "Canada",
    hero: SKYLINE_C,
    hook: "Where lifestyle founders build small but profitable.",
    localContext:
      "Victoria's economy mixes provincial government stability with a high concentration of lifestyle founders. The brands that win here pair calm aesthetics with disciplined unit economics. Our work is often a Complete Brand Identity System for founders ready to scale beyond the island.",
  },
];

export function getLocation(slug) {
  return SEO_LOCATIONS.find((l) => l.slug === slug) || null;
}
